/**
 * Returns the server's current epoch ms. The app supplies this (e.g. fetch
 * `/api/time`, parse `data.now`, `getTime()`) — the package does no I/O itself,
 * which keeps it pure-ish, runtime-agnostic, and trivially testable.
 */
export type ServerTimeFetcher = () => Promise<number>;

export interface ServerClock {
    /** Server-anchored now (epoch ms): local clock + cached offset. Before the
     *  first successful sync this is just the local clock (offset 0). */
    now(): number;
    /** True once an offset has been computed from a successful fetch. */
    isSynced(): boolean;
    /** Compute the offset once; dedupes concurrent calls and caches the result.
     *  Resolves to the offset in ms (server − local). */
    sync(): Promise<number>;
    /** Force a fresh fetch, ignoring the cache. Call when entering a countdown's
     *  final stretch or after tab resume, where a long-cached offset may have
     *  drifted (sleep/suspend skews the local clock). */
    refresh(): Promise<number>;
}

/**
 * Owns the device↔server time offset behind one small object. This is the
 * wall-clock-skew half of the honest-countdown fix (ADR 024): a buyer whose
 * laptop is minutes off still sees the true time-remaining because the
 * countdown is computed against `now()` here, not `Date.now()` directly.
 *
 * The package owns the offset; the consuming hook (PR3) owns *when* to
 * `refresh()` (final stretch / visibility resume) and anchors the per-tick
 * countdown to a monotonic `performance.now()`.
 *
 * @param fetcher Returns the server's epoch ms. On failure the clock keeps its
 *                last offset (or 0), so a `/api/time` outage degrades to the
 *                local clock rather than breaking the countdown.
 * @param nowFn Injectable local clock (defaults to `Date.now`) — for tests.
 *
 * @sideEffect Calls `fetcher` (network) on `sync`/`refresh`. `now`/`isSynced` are pure.
 */
export function createServerClock({
    fetcher,
    nowFn = () => Date.now(),
}: {
    fetcher: ServerTimeFetcher;
    nowFn?: () => number;
}): ServerClock {
    let offsetMs: number | null = null;
    let pending: Promise<number> | null = null;

    const doFetch = async (): Promise<number> => {
        const localAtSend = nowFn();
        try {
            const serverNow = await fetcher();
            if (Number.isFinite(serverNow)) {
                // Cristian's algorithm: `serverNow` is the server's clock at
                // request-processing time, so it corresponds to the MIDPOINT of
                // the round trip (assuming symmetric latency), not the moment
                // the response arrived. Anchoring to the midpoint removes the
                // systematic one-response-leg bias you'd get from `nowFn()` here.
                const localAtMidpoint = (localAtSend + nowFn()) / 2;
                offsetMs = serverNow - localAtMidpoint;
            }
            return offsetMs ?? 0;
        } catch {
            return offsetMs ?? 0;
        } finally {
            pending = null;
        }
    };

    return {
        now: () => nowFn() + (offsetMs ?? 0),
        isSynced: () => offsetMs != null,
        sync: () => {
            if (offsetMs != null) return Promise.resolve(offsetMs);
            if (!pending) pending = doFetch();
            return pending;
        },
        refresh: () => {
            if (!pending) pending = doFetch();
            return pending;
        },
    };
}
